import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import useUser from "../Util/useUser.js";
import { useNavItems, findNavItem } from "../Util/navRegistry.js";
import { buildRankIndex, rankIndex } from "../Util/rankOptions.js";
import styles from "../Modules/CommandPalette.module.css";

/**
 * "Go to any page" — Ctrl/⌘ K anywhere, or "/" when not typing in a field.
 * Lists exactly the menu pages this user may open (Util/navRegistry.js), ranked
 * like every other picker (Util/rankOptions.js), with recently visited pages
 * first. ↑/↓ move, Enter opens, Esc closes. Mounted once (Routes.js) while
 * signed in; anything can open it with openCommandPalette().
 */

const OPEN_EVENT = "virangi:command-palette";
export const openCommandPalette = () => window.dispatchEvent(new Event(OPEN_EVENT));

const MAX_RECENT = 6;
const recentKey = (user) => `virangi.recentPages.${user?.userid ?? user?.username ?? "anon"}`;

const readRecent = (key) => {
    try {
        const v = JSON.parse(window.localStorage.getItem(key) || "[]");
        return Array.isArray(v) ? v : [];
    } catch (_) {
        return [];
    }
};

const isTypingTarget = (el) =>
    !!el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName));

/** Recently visited menu pages for the signed-in user, newest first. */
export function useRecentNavItems() {
    const { user } = useUser();
    const items = useNavItems();
    const { pathname } = useLocation();
    const key = recentKey(user);
    const [recent, setRecent] = useState(() => readRecent(key));

    // Remember every menu page visited, however it was reached
    useEffect(() => {
        const hit = findNavItem(pathname, items);
        if (!hit) return;
        setRecent((prev) => {
            const next = [hit.to, ...prev.filter((p) => p !== hit.to)].slice(0, MAX_RECENT);
            try { window.localStorage.setItem(key, JSON.stringify(next)); } catch (_) { /* private mode */ }
            return next;
        });
    }, [pathname, items, key]);

    return useMemo(() => {
        const byPath = new Map(items.map((it) => [it.to, it]));
        return recent.map((p) => byPath.get(p)).filter(Boolean); // only pages still permitted
    }, [recent, items]);
}

function CommandPalette() {
    const history = useHistory();
    const items = useNavItems();
    const recent = useRecentNavItems();
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [active, setActive] = useState(0);
    const inputRef = useRef(null);
    const listRef = useRef(null);
    const restoreFocusRef = useRef(null);

    const show = useCallback(() => {
        restoreFocusRef.current = document.activeElement;
        setQuery("");
        setActive(0);
        setOpen(true);
    }, []);

    const close = useCallback(() => {
        setOpen(false);
        const el = restoreFocusRef.current;
        restoreFocusRef.current = null;
        if (el && typeof el.focus === "function") setTimeout(() => el.focus(), 0);
    }, []);

    // Global shortcuts + programmatic open
    useEffect(() => {
        const onKey = (e) => {
            if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === "k") {
                e.preventDefault();
                if (open) close(); else show();
            } else if (!open && e.key === "/" && !e.ctrlKey && !e.metaKey && !e.altKey && !isTypingTarget(e.target)) {
                e.preventDefault();
                show();
            }
        };
        window.addEventListener("keydown", onKey);
        window.addEventListener(OPEN_EVENT, show);
        return () => {
            window.removeEventListener("keydown", onKey);
            window.removeEventListener(OPEN_EVENT, show);
        };
    }, [open, show, close]);

    useEffect(() => {
        if (open) inputRef.current?.focus();
    }, [open]);

    const index = useMemo(
        () => buildRankIndex(items, { getLabel: (i) => i.label, getCode: null, getSub: (i) => i.trail.join(" ") }),
        [items]
    );

    const results = useMemo(() => {
        if (query.trim()) return rankIndex(index, query, 50);
        const seen = new Set(recent.map((r) => r.to));
        const rest = [...items]
            .filter((i) => !seen.has(i.to))
            .sort((a, b) => a.label.localeCompare(b.label));
        return [...recent, ...rest].slice(0, 50);
    }, [query, index, items, recent]);

    useEffect(() => { setActive(0); }, [query]);

    // Keep the highlighted row in view
    useEffect(() => {
        listRef.current?.querySelector(`[data-idx="${active}"]`)?.scrollIntoView?.({ block: "nearest" });
    }, [active]);

    const go = (item) => {
        if (!item) return;
        setOpen(false);
        restoreFocusRef.current = null;
        history.push(item.to);
    };

    const onInputKey = (e) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActive((a) => Math.min(a + 1, results.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((a) => Math.max(a - 1, 0));
        } else if (e.key === "PageDown") {
            e.preventDefault();
            setActive((a) => Math.min(a + 8, results.length - 1));
        } else if (e.key === "PageUp") {
            e.preventDefault();
            setActive((a) => Math.max(a - 8, 0));
        } else if (e.key === "Enter") {
            e.preventDefault();
            go(results[active]);
        } else if (e.key === "Escape") {
            e.preventDefault();
            close();
        } else if (e.key === "Tab") {
            e.preventDefault(); // focus stays in the palette
        }
    };

    if (!open) return null;

    const recentCount = query.trim() ? 0 : recent.length;
    const activeId = results[active] ? `cmdk-opt-${active}` : undefined;

    return (
        <div className={styles.backdrop} onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}>
            <div className={styles.panel} role="dialog" aria-modal="true" aria-label="Go to a page">
                <div className={styles.searchRow}>
                    <i className={`bi bi-search ${styles.searchIcon}`} aria-hidden="true" />
                    <input
                        ref={inputRef}
                        className={styles.input}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={onInputKey}
                        placeholder="Go to a page…"
                        role="combobox"
                        aria-expanded="true"
                        aria-controls="cmdk-list"
                        aria-activedescendant={activeId}
                        aria-autocomplete="list"
                        autoComplete="off"
                        spellCheck={false}
                        data-no-enter-advance
                    />
                    <kbd className={styles.kbd}>Esc</kbd>
                </div>

                <ul id="cmdk-list" ref={listRef} className={styles.list} role="listbox" aria-label="Pages">
                    {results.length === 0 && (
                        <li className={styles.empty}>
                            {items.length === 0 ? "Loading pages…" : `No page matches “${query}”`}
                        </li>
                    )}
                    {results.map((item, i) => (
                        <React.Fragment key={`${item.trail.join(">")}|${item.to}|${item.label}`}>
                            {i === 0 && recentCount > 0 && <li className={styles.section} role="presentation">Recent</li>}
                            {i === recentCount && recentCount > 0 && <li className={styles.section} role="presentation">All pages</li>}
                            <li
                                id={`cmdk-opt-${i}`}
                                data-idx={i}
                                role="option"
                                aria-selected={i === active}
                                className={`${styles.option} ${i === active ? styles.optionActive : ""}`}
                                onMouseMove={() => { if (i !== active) setActive(i); }}
                                onClick={() => go(item)}
                            >
                                <span className={styles.optionLabel}>{item.label}</span>
                                <span className={styles.optionTrail}>{item.trail.join(" › ")}</span>
                            </li>
                        </React.Fragment>
                    ))}
                </ul>

                <div className={styles.footer} aria-hidden="true">
                    <span><kbd className={styles.kbd}>↑</kbd><kbd className={styles.kbd}>↓</kbd> move</span>
                    <span><kbd className={styles.kbd}>Enter</kbd> open</span>
                    <span><kbd className={styles.kbd}>Esc</kbd> close</span>
                </div>
            </div>
        </div>
    );
}

export default CommandPalette;
